import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Download,
  Upload,
  Database,
  FileJson,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Shield,
  Info,
  Copy,
  FileDown,
  Code,
} from "lucide-react";

const COLLECTION_LABELS = {
  restaurants: "Restaurants",
  brands: "Brands",
  revenues: "Revenue Entries",
  revenue_categories: "Revenue Categories",
  expense_entries: "Expense Entries",
  expense_categories: "Expense Categories",
  royalty_entries: "Royalty Entries",
  platform_deductions: "Platform Deductions",
  head_office_expenses: "Head Office Expenses",
  ho_expense_categories: "HO Expense Categories",
  cash_handovers: "Cash Handovers",
  cash_adjustments: "Cash Adjustments",
  employees: "Employees",
  restaurant_targets: "Restaurant Targets",
  credentials: "Credentials",
  users: "Users",
  anomaly_settings: "Anomaly Settings",
  platforms: "Platforms",
  audit_logs: "Audit Logs",
};

const DataManager = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState("json");
  const [importCollection, setImportCollection] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { fetchCollections(); }, []);

  const fetchCollections = async () => {
    try {
      const res = await axiosInstance.get("/export/collections/list");
      setCollections(res.data);
    } catch (e) {
      toast.error("Failed to load collections");
    }
  };

  const handleExportAll = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/export/all", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `full_backup_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Full backup downloaded!");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCollection = async (key) => {
    try {
      const res = await axiosInstance.get(`/export/${key}?format=${exportFormat}`, { responseType: "blob" });
      const ext = exportFormat === "csv" ? "csv" : "json";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}_${new Date().toISOString().split("T")[0]}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${COLLECTION_LABELS[key] || key} exported!`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const fetchTemplate = async (collection) => {
    setLoadingTemplate(true);
    setTemplateData(null);
    try {
      const res = await axiosInstance.get(`/export/template/${collection}`);
      setTemplateData(res.data);
    } catch (e) {
      setTemplateData(null);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleCollectionChange = (value) => {
    setImportCollection(value);
    setImportResult(null);
    if (value) fetchTemplate(value);
    else setTemplateData(null);
  };

  const downloadTemplate = () => {
    if (!templateData?.sample) return;
    const template = [templateData.sample];
    const content = JSON.stringify(template, null, 2);
    const blob = new Blob([content], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${importCollection}_template.json`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded — edit and re-upload with new IDs");
  };

  const copyFormatToClipboard = () => {
    if (!templateData?.sample) return;
    const template = [templateData.sample];
    navigator.clipboard.writeText(JSON.stringify(template, null, 2));
    toast.success("Format copied to clipboard");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!importCollection) {
      toast.error("Please select a collection first");
      return;
    }
    setPendingFile(file);
    setConfirmDialog(true);
  };

  const handleImportConfirm = async () => {
    setConfirmDialog(false);
    if (!pendingFile || !importCollection) return;

    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", pendingFile);
      const res = await axiosInstance.post(`/import/${importCollection}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.imported} records`);
      fetchCollections();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const totalRecords = collections.reduce((s, c) => s + c.count, 0);

  return (
    <div className="space-y-6" data-testid="data-manager">
      {/* Header */}
      <Card className="shadow-lg border-0">
        <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-900 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Database className="w-6 h-6" />
            Data Manager
          </CardTitle>
          <CardDescription className="text-slate-300">
            Export backups and import data across {collections.length} collections ({totalRecords.toLocaleString()} total records)
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Export Section */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Download className="w-5 h-5 text-blue-600" />
                Export Data
              </CardTitle>
              <CardDescription>Download backups of your data</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="w-28" data-testid="export-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">JSON</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={handleExportAll}
                disabled={loading}
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                data-testid="export-all-btn"
              >
                <Download className="w-4 h-4" />
                {loading ? "Exporting..." : "Export All (JSON)"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Collection</TableHead>
                <TableHead className="text-right">Records</TableHead>
                <TableHead className="text-right w-32">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collections.map((col) => (
                <TableRow key={col.key}>
                  <TableCell className="font-medium">{COLLECTION_LABELS[col.key] || col.key}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">{col.count}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExportCollection(col.key)}
                      disabled={col.count === 0}
                      className="gap-1 text-blue-600"
                    >
                      {exportFormat === "csv" ? <FileSpreadsheet className="w-4 h-4" /> : <FileJson className="w-4 h-4" />}
                      Export
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Import Section */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="w-5 h-5 text-green-600" />
            Import Data
          </CardTitle>
          <CardDescription>Add new records from a file (append only — existing data is never modified or deleted)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Steps */}
          <div className="bg-slate-50 rounded-lg p-5 border">
            <h4 className="font-semibold text-sm text-slate-700 mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" /> How Import Works
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {[
                { step: "1", title: "Select Collection", desc: "Choose which data type to import" },
                { step: "2", title: "Upload File", desc: "JSON array or CSV file" },
                { step: "3", title: "Confirm", desc: "Review and confirm the import" },
                { step: "4", title: "Validation", desc: "Each record is checked for an 'id' field" },
                { step: "5", title: "Append", desc: "New records added, duplicates skipped" },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {s.step}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{s.title}</p>
                    <p className="text-xs text-slate-500">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Safety note */}
          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
            <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-green-800">Safe & Non-destructive</p>
              <p className="text-green-700 text-xs mt-1">
                Import only <strong>appends new records</strong>. Records with duplicate IDs are automatically skipped.
                No existing data is modified, overwritten, or deleted.
              </p>
            </div>
          </div>

          {/* Import controls */}
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label className="text-sm">Step 1: Select Collection</Label>
              <Select value={importCollection} onValueChange={handleCollectionChange}>
                <SelectTrigger data-testid="import-collection">
                  <SelectValue placeholder="Choose collection..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(COLLECTION_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label className="text-sm">Step 2: Upload File (.json or .csv)</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".json,.csv"
                onChange={handleFileSelect}
                disabled={!importCollection || importing}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-file-input"
              />
            </div>
          </div>

          {/* Format Preview - shows when collection is selected */}
          {importCollection && (
            <div className="border rounded-lg bg-white overflow-hidden" data-testid="format-preview">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Expected Format — {COLLECTION_LABELS[importCollection]}
                </h4>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyFormatToClipboard}
                    disabled={!templateData?.sample}
                    className="gap-1 text-xs h-7"
                    data-testid="copy-format-btn"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    disabled={!templateData?.sample}
                    className="gap-1 text-xs h-7"
                    data-testid="download-template-btn"
                  >
                    <FileDown className="w-3 h-3" /> Download Template
                  </Button>
                </div>
              </div>
              {loadingTemplate ? (
                <div className="p-4 text-sm text-gray-500">Loading format...</div>
              ) : templateData?.fields?.length > 0 ? (
                <div className="p-4 space-y-3">
                  {/* Fields table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Field</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Type</th>
                          <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Example Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {templateData.fields.map((field) => (
                          <tr key={field.name} className="border-b last:border-0">
                            <td className="py-1.5 px-2 font-mono text-slate-800 font-medium">
                              {field.name}
                              {field.name === "id" && (
                                <span className="ml-1 text-red-500 font-sans text-[10px] font-semibold">REQUIRED</span>
                              )}
                            </td>
                            <td className="py-1.5 px-2">
                              <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                                {field.type}
                              </Badge>
                            </td>
                            <td className="py-1.5 px-2 text-slate-500 font-mono max-w-[300px] truncate">
                              {typeof field.example === "object"
                                ? JSON.stringify(field.example).substring(0, 80)
                                : String(field.example).substring(0, 80)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* JSON sample */}
                  <details className="group">
                    <summary className="cursor-pointer text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                      <FileJson className="w-3 h-3" />
                      View full JSON example
                    </summary>
                    <pre className="mt-2 p-3 bg-slate-900 text-green-300 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto">
                      {JSON.stringify([templateData.sample], null, 2)}
                    </pre>
                  </details>
                  <p className="text-xs text-slate-500">
                    Your file should be a <strong>JSON array</strong> of objects in this format, or a <strong>CSV</strong> with matching column headers.
                    Each record <strong>must</strong> have a unique <code className="bg-slate-100 px-1 rounded">id</code> field. Use <strong>Download Template</strong> to get a ready-to-edit file.
                  </p>
                </div>
              ) : templateData && !templateData.fields?.length ? (
                <div className="p-4 text-sm text-slate-500">
                  This collection is empty. Export a populated collection first, or use this JSON format:
                  <pre className="mt-2 p-2 bg-slate-100 rounded text-xs font-mono">[{`{ "id": "unique-id-here", ... }`}]</pre>
                </div>
              ) : null}
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="border rounded-lg p-4 bg-white space-y-3" data-testid="import-result">
              <h4 className="font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Import Complete — {COLLECTION_LABELS[importResult.collection] || importResult.collection}
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-gray-50 rounded text-center">
                  <p className="text-2xl font-bold text-gray-800">{importResult.total_in_file}</p>
                  <p className="text-xs text-gray-500">Total in File</p>
                </div>
                <div className="p-3 bg-green-50 rounded text-center">
                  <p className="text-2xl font-bold text-green-700">{importResult.imported}</p>
                  <p className="text-xs text-green-600">Imported</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded text-center">
                  <p className="text-2xl font-bold text-yellow-700">{importResult.skipped}</p>
                  <p className="text-xs text-yellow-600">Skipped (Duplicates)</p>
                </div>
              </div>
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="space-y-1 mt-2">
                  <p className="text-sm font-medium text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> Errors:
                  </p>
                  <div className="max-h-32 overflow-y-auto text-xs text-red-700 bg-red-50 p-2 rounded">
                    {importResult.errors.map((err, i) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Import Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Import</DialogTitle>
            <DialogDescription>
              You are about to import data into <strong>{COLLECTION_LABELS[importCollection]}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm space-y-2">
              <p><strong>File:</strong> {pendingFile?.name}</p>
              <p><strong>Target:</strong> {COLLECTION_LABELS[importCollection]}</p>
              <p><strong>Mode:</strong> Append only (no data will be deleted or overwritten)</p>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
              <p className="font-medium">What will happen:</p>
              <ul className="list-disc list-inside text-xs mt-1 space-y-1">
                <li>Each record in the file will be validated</li>
                <li>Records with an existing ID will be <strong>skipped</strong></li>
                <li>New records will be <strong>added</strong> to the collection</li>
                <li>No existing data will be modified or removed</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmDialog(false); setPendingFile(null); }}>Cancel</Button>
            <Button onClick={handleImportConfirm} disabled={importing} className="gap-2 bg-green-600 hover:bg-green-700">
              <Upload className="w-4 h-4" />
              {importing ? "Importing..." : "Import Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManager;
